import React from 'react';
import {
  Box, HStack, Icon, Pressable, Text, VStack,
} from '@gluestack-ui/themed';
import { ChevronLeft, ChevronRight } from 'lucide-react-native';
import { useTranslation } from 'react-i18next';
import { OralExamFillKindBadge } from '@/common/components/OralExamFillKindBadge';
import { OralExamQuestionText, getOralExamQuestionTitle } from '@/common/components/OralExamQuestionText';
import { PREVIEW_CUE } from '@/common/components/oralExamPreviewQuestion';
import { questionHasFill, type LinkedOralExamQuestion } from './linkedOralExamQuestions';

interface OralExamQuestionPagerProps {
  questions: LinkedOralExamQuestion[];
  isMobileView?: boolean;
  testID?: string;
}

const PagerArrow: React.FC<{
  direction: 'next' | 'previous';
  disabled: boolean;
  onPress: () => void;
}> = ({ direction, disabled, onPress }) => {
  const { t } = useTranslation();
  const isNext = direction === 'next';
  return (
    <Pressable
      onPress={onPress}
      disabled={disabled}
      accessibilityRole="button"
      accessibilityState={{ disabled }}
      accessibilityLabel={t(isNext ? 'student.task.essayStart.nextQuestion' : 'student.task.essayStart.previousQuestion')}
      w={36}
      h={36}
      alignItems="center"
      justifyContent="center"
      borderRadius="$full"
      $hover-bg={disabled ? undefined : '$bgHover'}
      testID={`oral-exam-pager-${direction}`}
    >
      <Icon
        as={isNext ? ChevronRight : ChevronLeft}
        size="md"
        color={disabled ? '$textDisabled' : '$textSubtle'}
      />
    </Pressable>
  );
};

const FillHint: React.FC<{ testID?: string }> = ({ testID }) => {
  const { t } = useTranslation();
  const [before, ...rest] = t('student.task.essayStart.fillGap').split(PREVIEW_CUE);
  return (
    <Text size="xs" color="$textSubtle" testID={testID}>
      {before}
      {rest.length > 0 ? (
        <>
          <OralExamFillKindBadge />
          {rest.join(PREVIEW_CUE)}
        </>
      ) : null}
    </Text>
  );
};

/** Lead in the heading face, stem as body text, sub-questions secondary. */
const QuestionBody: React.FC<{
  question: LinkedOralExamQuestion;
  testIDPrefix?: string;
}> = ({ question, testIDPrefix }) => {
  const text = question.question ?? '';
  const title = getOralExamQuestionTitle(text);
  return (
    <VStack space="sm">
      {title ? (
        <Text fontFamily="$heading" color="$textHeading" testID={testIDPrefix && `${testIDPrefix}-title`}>
          {title}
        </Text>
      ) : null}
      <OralExamQuestionText
        question={text}
        size="md"
        promptSize="sm"
        promptColor="$textSubtle"
        hideTitle={Boolean(title)}
        showFillSlot
        fillKind={question.fillKind}
      />
    </VStack>
  );
};

/**
 * The linked oral exam questions as one labelled card, one question at a time, in full.
 * The card keeps the height of the tallest question so paging never moves the page.
 */
export const OralExamQuestionPager: React.FC<OralExamQuestionPagerProps> = ({
  questions,
  isMobileView = false,
  testID = 'oral-exam-question-pager',
}) => {
  const { t } = useTranslation();
  const [index, setIndex] = React.useState(0);
  const [heights, setHeights] = React.useState<Record<number, number>>({});
  const count = questions.length;
  const safeIndex = Math.min(index, Math.max(0, count - 1));
  const question = questions[safeIndex];
  if (!question) return null;

  const showPager = count > 1;
  const tallest = Math.max(0, ...Object.values(heights));
  const anyHasFill = questions.some(questionHasFill);
  const currentHasFill = questionHasFill(question);

  return (
    <VStack space="sm" width="100%">
      <VStack
        space="md"
        width="100%"
        p={isMobileView ? '$4' : '$6'}
        bg="$bgSubtle"
        borderRadius="$lg"
        borderWidth={1}
        borderColor="$borderLight"
        testID={testID}
      >
        <HStack alignItems="center" justifyContent="space-between" minHeight={20}>
          <Text
            size="xs"
            color="$textHint"
            textTransform="uppercase"
            letterSpacing="$md"
            testID={`${testID}-label`}
          >
            {t('student.task.essayStart.questionLabel')}
          </Text>
          {showPager ? (
            <HStack alignItems="center" mr={-8} my={-8}>
              <PagerArrow
                direction="previous"
                disabled={safeIndex === 0}
                onPress={() => setIndex(safeIndex - 1)}
              />
              <Text size="sm" color="$textSubtle" testID={`${testID}-count`}>
                {t('student.task.essayStart.pagerCount', { index: safeIndex + 1, count })}
              </Text>
              <PagerArrow
                direction="next"
                disabled={safeIndex === count - 1}
                onPress={() => setIndex(safeIndex + 1)}
              />
            </HStack>
          ) : null}
        </HStack>
        <Box position="relative">
          {showPager ? questions.map((measured, measuredIndex) => (
            <Box
            // Invisible copies, one per question, only to measure the tallest.
              key={measured.questionId ?? measuredIndex}
              position="absolute"
              top={0}
              left={0}
              right={0}
              opacity={0}
              pointerEvents="none"
              aria-hidden
              importantForAccessibility="no-hide-descendants"
              onLayout={(event) => {
                const { height } = event.nativeEvent.layout;
                setHeights((previous) => (
                  previous[measuredIndex] === height ? previous : { ...previous, [measuredIndex]: height }
                ));
              }}
            >
              <QuestionBody question={measured} />
            </Box>
          )) : null}
          <Box minHeight={tallest || undefined} testID={`${testID}-question-${safeIndex}`}>
            <QuestionBody question={question} testIDPrefix={testID} />
          </Box>
        </Box>
      </VStack>
      {anyHasFill ? (
        // Kept in place while paging to a question without a gap, so nothing below moves.
        <Box
          px={isMobileView ? '$4' : '$6'}
          opacity={currentHasFill ? 1 : 0}
          aria-hidden={!currentHasFill}
          importantForAccessibility={currentHasFill ? 'auto' : 'no-hide-descendants'}
        >
          <FillHint testID={currentHasFill ? 'essay-start-fill-gap' : undefined} />
        </Box>
      ) : null}
    </VStack>
  );
};

export default OralExamQuestionPager;
